// MACHINERY V2 (Stroje) — one katalog model card below 1280 (M2.16c).
//
// Rendered truth: mobile/screenshots/16-katalog-list.png. Reference impl:
// mobile/machinery-mobile-katalog.jsx `MbkCard` — VALUES taken, code not.
//
// >>> A SEPARATE COMPONENT, NOT A BRANCH INSIDE CatalogModelCardV2. <<<
// The two cards are different LAYOUTS, not one layout at two sizes: the desktop
// card stacks a category-badge row, a thumb+name row, a spec row and a footer
// carrying two capability badges, a doc phrase, "View details" and an add
// button; the board's card puts the thumb LEFT of an eyebrow/name/brand stack
// with the action in the corner, and ends on a readiness row. Threading that
// through one component means a boolean in every block. The precedent is
// fleet/FleetCard.tsx vs fleet/FleetCardCompact.tsx, and the payoff is the one
// this sub-phase must guarantee: CatalogModelCardV2.tsx is not edited at all, so
// the desktop card cannot regress.
//
// Everything shareable IS shared — `CatalogThumb`, `useCategoryLabel`,
// `CatalogKeySpecs` and `catalogThumbUrl` all come from catalogAtoms.tsx, and
// the readiness tier comes from readiness/readinessTier.ts. Nothing is restated.
//
// THE READINESS ROW — the surface that AMENDS the `no readiness text row` lock
// below 1280, and the disclosure trigger this card would otherwise lack — is
// CatalogCardReadinessRow.tsx. The full reasoning, and the pointer back into the
// amended lock itself, lives in that file's header.
//
// ============================================================
//  >>> DELIBERATE DEVIATION: NO "N vo flotile" PILL. READ BEFORE ADDING ONE.
// ============================================================
// The board draws a "3 vo flotile" pill — how many of THE USER'S OWN machines
// are this model. That number is not on the wire. Two fields look like it and
// neither is:
//   · `CatalogModelListItem.machineCount` does not exist; `machineCount` at
//     models/models.ts:328 is on `CatalogManufacturer` and counts CATALOG MODELS
//     PER MANUFACTURER — a different entity and a different unit;
//   · `unitsOnPlatform` (models.ts:244) is tempting because the desktop card
//     already renders it, but it is PLATFORM-WIDE, not this customer's:
//     `CatalogModelsRepository.GetUnitsOnPlatformCountsAsync` (:197-211, read in
//     GoToStockServer-machinery at this session's checkout) groups
//     `_context.Machines` by `CatalogModelId` with NO customer predicate at all.
//     Printing it as "vo flotile" would tell a customer that other tenants'
//     machines are in their fleet.
// A backend ask is filed. Until it lands the pill is OMITTED — an absent pill is
// honest, a confident wrong number is not. Do not "finish" this from either
// field.
//
// >>> NO READINESS BANNER HERE, EITHER. <<< The board draws no ribbon on this
// card: the tier row at the bottom is the mobile card's readiness surface, and a
// ribbon plus a row would state the same fact twice on the narrowest screen in
// the module. Nothing about ribbon PRECEDENCE is decided here or anywhere but
// `bannerTier()` — this card simply does not draw one.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { formatManufacturerWithHierarchy } from "../../../../shared/utils/machineUtils";
import { CatalogModelListItem } from "../../models/models";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import CatalogCardReadinessRow from "./CatalogCardReadinessRow";
import {
  CatalogKeySpecs,
  CatalogThumb,
  catalogThumbUrl,
  useCategoryLabel,
} from "./catalogAtoms";

export interface CatalogModelCardMobileProps {
  model: CatalogModelListItem;
  /** Derived ONCE by the page via `deriveReadinessPresentation` — never here. */
  presentation: MachineryV2ReadinessPresentation;
  onOpen: (model: CatalogModelListItem) => void;
  onOpenReadiness: (model: CatalogModelListItem) => void;
  canWrite: boolean;
  onAdd: (model: CatalogModelListItem) => void;
}

const CatalogModelCardMobile: React.FC<CatalogModelCardMobileProps> = ({
  model,
  presentation,
  onOpen,
  onOpenReadiness,
  canWrite,
  onAdd,
}) => {
  const { translate } = useTranslation("myMachinery");
  const categoryLabel = useCategoryLabel()(model.categoryCode);

  const manufacturerLine = model.manufacturer
    ? formatManufacturerWithHierarchy(
        model.manufacturer.name,
        model.manufacturer.parentManufacturerName
      )
    : "";

  // >>> EVERY EMBEDDED CONTROL SWALLOWS BOTH ACTIVATION PATHS. <<< This card is
  // itself keyboard-activatable, so a focused child button's Enter/Space
  // produces a click that bubbles to the card exactly like a mouse click does.
  // stopPropagation ONLY — NEVER preventDefault on a button's keydown: the
  // browser synthesises that button's click FROM this key event, so preventing
  // the default would kill the control outright. Canonical shape:
  // readiness/CapabilityBadge.tsx `onClick` / `onKeyDown`.
  const swallowKeys = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") e.stopPropagation();
  };
  const activate = (e: React.SyntheticEvent, run: () => void) => {
    e.stopPropagation();
    run();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(model)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          // Correct on THIS element and not on the children: the card is a
          // <div role="button">, which the browser does not activate on a key —
          // preventDefault here only stops Space from scrolling the page.
          e.preventDefault();
          onOpen(model);
        }
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
        borderRadius: 12,
        overflow: "hidden",
        cursor: "pointer",
        background: "var(--card)",
        border: "1px solid var(--border)",
        boxShadow: "var(--shadow)",
        padding: "11px 13px",
      }}
    >
      <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
        <CatalogThumb
          url={catalogThumbUrl(model)}
          alt={model.modelName}
          width={66}
          height={50}
        />

        <div style={{ minWidth: 0, flex: 1 }}>
          {categoryLabel && (
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                color: "var(--faint)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {categoryLabel}
            </div>
          )}
          <div
            title={model.modelName}
            style={{
              fontSize: 14.5,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
              marginTop: 2,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {model.modelName}
          </div>
          {manufacturerLine && (
            <div
              title={manufacturerLine}
              style={{
                fontSize: 11,
                color: "var(--muted)",
                marginTop: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {manufacturerLine}
            </div>
          )}
        </div>

        {/* The board draws a kebab here. It ships as the ONE action that kebab
            would contain — the same Write-gated add the desktop card carries —
            because a menu whose only item repeats its own trigger is the defect
            FleetMobileHeader.tsx:134-138 records. */}
        {canWrite && (
          <button
            type="button"
            className="btn btn-mod"
            onClick={(e) => activate(e, () => onAdd(model))}
            onKeyDown={swallowKeys}
            aria-label={translate("myMachinery:addMachine")}
            title={translate("myMachinery:addMachine")}
            style={{
              // >= 44px BOTH axes — an icon-only control has no label to widen
              // it, so both are declared.
              width: 44,
              height: 44,
              minWidth: 44,
              padding: 0,
              borderRadius: 10,
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            <span aria-hidden>+</span>
          </button>
        )}
      </div>

      <div style={{ marginTop: 9, minWidth: 0 }}>
        <CatalogKeySpecs model={model} />
      </div>

      <CatalogCardReadinessRow
        presentation={presentation}
        onOpenReadiness={() => onOpenReadiness(model)}
        onKeyDown={swallowKeys}
        onActivate={activate}
      />
    </div>
  );
};

export default CatalogModelCardMobile;
